import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar, Clock, User, Phone, Mail, Car, Edit } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const EditBookingModal = ({ isOpen, onClose, booking, onBookingUpdated }) => {
  const { toast } = useToast();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    date: '',
    time_slot: '',
    slot_number: 1,
    vehicle_type: 'personbil',
    notes: '',
    status: 'confirmed'
  });

  // Update form when booking prop changes
  useEffect(() => {
    if (booking) {
      setFormData({
        customer_name: booking.customer_name || '',
        customer_email: booking.customer_email || '',
        customer_phone: booking.customer_phone || '',
        date: booking.date || '',
        time_slot: booking.time_slot || '',
        slot_number: booking.slot_number || 1,
        vehicle_type: booking.vehicle_type || 'personbil',
        notes: booking.notes || '',
        status: booking.status || 'confirmed'
      });
    }
  }, [booking]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!formData.customer_name.trim()) {
      toast({
        title: "Mangler informasjon",
        description: "Vennligst fyll inn kundenavn",
        variant: "destructive"
      });
      return false;
    }
    if (!formData.customer_email.trim()) {
      toast({
        title: "Mangler informasjon",
        description: "Vennligst fyll inn e-post",
        variant: "destructive"
      });
      return false;
    }
    if (!formData.customer_phone.trim()) {
      toast({
        title: "Mangler informasjon",
        description: "Vennligst fyll inn telefonnummer",
        variant: "destructive"
      });
      return false;
    }
    if (!formData.date) {
      toast({
        title: "Mangler informasjon",
        description: "Vennligst velg dato",
        variant: "destructive"
      });
      return false;
    }
    if (!formData.time_slot) {
      toast({
        title: "Mangler informasjon",
        description: "Vennligst velg tidspunkt",
        variant: "destructive"
      });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!booking?.id) {
      toast({
        title: "Feil",
        description: "Booking ID mangler",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.put(`${API}/bookings/${booking.id}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      toast({
        title: "Booking Oppdatert! ✅",
        description: `Booking ${booking.booking_number} er oppdatert.`,
      });

      if (onBookingUpdated) {
        onBookingUpdated(response.data);
      }
      
      onClose();
    } catch (error) {
      console.error('Error updating booking:', error);
      const errorMsg = error.response?.data?.detail || "Kunne ikke oppdatere booking";
      toast({
        title: "Feil",
        description: errorMsg,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Generate time slots (07:00 - 16:00)
  const timeSlots = [];
  for (let hour = 7; hour < 16; hour++) {
    timeSlots.push(`${hour.toString().padStart(2, '0')}:00`);
    timeSlots.push(`${hour.toString().padStart(2, '0')}:30`);
  }

  // Generate slot numbers (1-6)
  const slotNumbers = [1, 2, 3, 4, 5, 6];

  if (!booking) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center space-x-2">
            <Edit className="w-6 h-6 text-red-400" />
            <span>Rediger Booking</span>
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Booking #{booking.booking_number}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Customer Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-red-400 flex items-center space-x-2">
              <User className="w-5 h-5" />
              <span>Kundeinformasjon</span>
            </h3>

            <div className="space-y-2">
              <Label htmlFor="edit_customer_name" className="text-gray-300">Navn *</Label>
              <Input
                id="edit_customer_name"
                value={formData.customer_name}
                onChange={(e) => handleChange('customer_name', e.target.value)}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="Fullt navn"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_customer_email" className="text-gray-300">E-post *</Label>
                <Input
                  id="edit_customer_email"
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => handleChange('customer_email', e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="epost@example.com"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_customer_phone" className="text-gray-300">Telefon *</Label>
                <Input
                  id="edit_customer_phone"
                  type="tel"
                  value={formData.customer_phone}
                  onChange={(e) => handleChange('customer_phone', e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="12345678"
                  required
                />
              </div>
            </div>
          </div>

          {/* Booking Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-red-400 flex items-center space-x-2">
              <Calendar className="w-5 h-5" />
              <span>Booking detaljer</span>
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_date" className="text-gray-300">Dato *</Label>
                <Input
                  id="edit_date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleChange('date', e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_time_slot" className="text-gray-300">Tidspunkt *</Label>
                <Select value={formData.time_slot} onValueChange={(value) => handleChange('time_slot', value)}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                    <SelectValue placeholder="Velg tid" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white">
                    {timeSlots.map(slot => (
                      <SelectItem key={slot} value={slot}>{slot}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_slot_number" className="text-gray-300">Plass nummer *</Label>
                <Select value={formData.slot_number.toString()} onValueChange={(value) => handleChange('slot_number', parseInt(value))}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                    <SelectValue placeholder="Velg plass" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white">
                    {slotNumbers.map(num => (
                      <SelectItem key={num} value={num.toString()}>Plass {num}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_vehicle_type" className="text-gray-300">Kjøretøy type *</Label>
                <Select value={formData.vehicle_type} onValueChange={(value) => handleChange('vehicle_type', value)}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                    <SelectValue placeholder="Velg type" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white">
                    <SelectItem value="personbil">Personbil</SelectItem>
                    <SelectItem value="varebil">Varebil</SelectItem>
                    <SelectItem value="tungbil">Tungbil</SelectItem>
                    <SelectItem value="tilhenger">Tilhenger</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_status" className="text-gray-300">Status *</Label>
              <Select value={formData.status} onValueChange={(value) => handleChange('status', value)}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                  <SelectValue placeholder="Velg status" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700 text-white">
                  <SelectItem value="confirmed">Bekreftet</SelectItem>
                  <SelectItem value="completed">Fullført</SelectItem>
                  <SelectItem value="cancelled">Kansellert</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_notes" className="text-gray-300">Notat (valgfritt)</Label>
              <textarea
                id="edit_notes"
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
                placeholder="Tilleggsnotat..."
                rows={3}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-700">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="border-gray-600 text-gray-300 hover:bg-gray-800"
              disabled={loading}
            >
              Avbryt
            </Button>
            <Button
              type="submit"
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Lagrer...
                </>
              ) : (
                <>
                  <Edit className="w-4 h-4 mr-2" />
                  Lagre Endringer
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditBookingModal;
